// Colour swatches on product tiles (snippets/card-swatches.liquid).
//
// Clicking a square swaps the whole tile in place for that colour's product,
// rendered by a card-only view (/products/<handle>?view=card…, see
// sections/main-product-card.liquid). Hovering or focusing a square only
// prefetches that view, so the click is usually instant. If the view can't be
// fetched or renders no tile, the click falls back to the product page.
//
// The tile re-initialises itself once inserted: its <data-island> root
// hydrates through Alpine.initTree when connected, <image-with-placeholder> is
// a custom element, and quick buy runs from its own islands. Nothing to re-run
// here.
//
// The view can't know which section it lands in, so the host-specific bits
// are reapplied from the tile being replaced (buildTile).

const SECTION_TOKEN = '__CARDVIEW__';
const QUICK_BUY_CLASSES_TOKEN = '__CARDVIEW_QB__';

// One request per product view, shared by prefetch and click.
const cardViews = new Map();

function cardViewUrl(url, view) {
  return `${url}${url.includes('?') ? '&' : '?'}view=${encodeURIComponent(view)}`;
}

function fetchCardView(url, view, sizes) {
  const key = cardViewUrl(url, view);
  if (!cardViews.has(key)) {
    const request = fetch(key, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Card view ${response.status}`);
        return response.text();
      })
      .then((html) => {
        // Warm the image cache with the main image the swapped tile will show,
        // at the size the host grid asks for.
        const image = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-product-tile] image-with-placeholder img');
        if (image && image.srcset) {
          const preload = new Image();
          if (sizes) preload.sizes = sizes;
          preload.srcset = image.srcset;
          preload.src = image.src;
        }
        return html;
      });
    request.catch(() => cardViews.delete(key));
    cardViews.set(key, request);
  }
  return cardViews.get(key);
}

class CardSwatches extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerover', this.onPrefetch);
    this.addEventListener('focusin', this.onPrefetch);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerover', this.onPrefetch);
    this.removeEventListener('focusin', this.onPrefetch);
  }

  get tile() {
    return this.closest('[data-product-tile]');
  }

  get hostSizes() {
    return this.tile?.querySelector('img[sizes]')?.getAttribute('sizes') || '';
  }

  onPrefetch(event) {
    const swatch = event.target.closest('.card-swatch');
    if (swatch && swatch.getAttribute('aria-pressed') !== 'true') {
      fetchCardView(swatch.dataset.url, this.dataset.view, this.hostSizes).catch(() => {});
    }
  }

  async onClick(event) {
    const swatch = event.target.closest('.card-swatch');
    if (!swatch || swatch.getAttribute('aria-pressed') === 'true') return;
    if (this.getAttribute('aria-busy') === 'true') return;

    // A click with detail 0 comes from the keyboard (Enter/Space).
    const fromKeyboard = event.detail === 0;
    this.setAttribute('aria-busy', 'true');
    try {
      const html = await fetchCardView(swatch.dataset.url, this.dataset.view, this.hostSizes);
      const host = this.tile;
      if (!host) throw new Error('No host tile');
      const tile = this.buildTile(html, host);
      host.replaceWith(tile);
      if (fromKeyboard) tile.querySelector('.card-swatch[aria-pressed="true"]')?.focus();
    } catch (error) {
      // No tile to swap in (fetch failed, or the view rendered nothing):
      // go to that colour's product page instead. Not busy any more, in case
      // the page comes back from the back/forward cache.
      this.removeAttribute('aria-busy');
      window.location.href = swatch.dataset.url;
    }
  }

  // Parses the card view and reapplies what depends on the host section.
  buildTile(html, host) {
    const { sectionId = '', quickBuy, quickBuyClasses = '' } = this.dataset;

    // Section-scoped ids (quick buy drawer and form ids): the view renders a
    // token in place of its own section id.
    const safeSectionId = /^[\w-]*$/.test(sectionId) ? sectionId : '';
    const doc = new DOMParser().parseFromString(
      html.replaceAll(SECTION_TOKEN, safeSectionId),
      'text/html'
    );
    const tile = doc.querySelector('[data-product-tile]');
    if (!tile) throw new Error('Empty card view');

    // Colour scheme of the host section.
    const scheme = host.getAttribute('data-color-scheme');
    if (scheme === null) tile.removeAttribute('data-color-scheme');
    else tile.setAttribute('data-color-scheme', scheme);

    // Image sizes of the host grid.
    const sizes = host.querySelector('img[sizes]')?.getAttribute('sizes');
    if (sizes) {
      tile.querySelectorAll('img[sizes]').forEach((img) => img.setAttribute('sizes', sizes));
    }

    // Classes that only depend on the host's parameters (products per row on
    // mobile): copied from the matching elements of the host tile.
    const hostSynced = host.querySelectorAll('[data-card-sync]');
    const tileSynced = tile.querySelectorAll('[data-card-sync]');
    if (hostSynced.length === tileSynced.length) {
      tileSynced.forEach((el, i) => {
        el.className = hostSynced[i].className;
      });
    }

    // Forced crop (carousel "Image crop"): the view renders the natural image,
    // the host a cropped one. Copy the crop wrapper and image classes.
    const hostMain = host.querySelector('image-with-placeholder');
    const tileMain = tile.querySelector('image-with-placeholder');
    if (hostMain?.style.paddingBottom && tileMain && !tileMain.style.paddingBottom) {
      tileMain.className = hostMain.className;
      tileMain.setAttribute('style', hostMain.getAttribute('style'));
      const hostImg = hostMain.querySelector('img');
      const tileImg = tileMain.querySelector('img');
      if (hostImg && tileImg) {
        tileImg.className = hostImg.className;
        tileImg.classList.remove('is-complete');
      }
    }

    // Quick buy: the view always renders it; drop it when the host has none,
    // otherwise give the buttons the host section's style.
    if (quickBuy === 'true') {
      tile.querySelectorAll(`[class*="${QUICK_BUY_CLASSES_TOKEN}"]`).forEach((el) => {
        el.className = el.className.replace(QUICK_BUY_CLASSES_TOKEN, quickBuyClasses);
      });
    } else {
      tile.querySelectorAll('[data-card-quick-buy]').forEach((el) => el.remove());
    }

    // The swapped tile keeps fetching the same kind of view (e.g. second image
    // first) with the same host settings.
    const swatches = tile.querySelector('card-swatches');
    if (swatches) Object.assign(swatches.dataset, this.dataset);

    return document.adoptNode(tile);
  }
}

if (!customElements.get('card-swatches')) {
  customElements.define('card-swatches', CardSwatches);
}
